import React, { useMemo } from 'react'
import useApiService from 'services/api'
import LoadingScreen from 'components/Layout/LoadingScreen'
import {
  CompanyPageLayout,
  CompanyContentSections,
} from 'components/NewCompanyPage'
import { UnmatchedDoorsModal } from 'components/NewCompanyPage/Modals/UnmatchedDoorsModal'
import useDocumentTitle from 'components/useDocumentTitle'
import {
  MetricCard,
  StatsCard,
  Divider,
  Rating,
  DemographicsSection,
  DescriptionAndContactInfo,
} from 'components/NewCompanyPage/Sidebar/shared'
import { formatInteger, formatAddress } from 'utils/formatting'
import { useHideFooter } from 'context/layout'

export function NewNursingHomePage({
  nursingHomeID,
}: {
  nursingHomeID?: number
}) {
  const api = useApiService()
  const { data, isLoading } = api.useGetNursingHome(nursingHomeID)

  // Hide footer on company pages (must be called before loading check)
  useHideFooter()

  // Set document title
  useDocumentTitle(
    `${data?.nursinghome?.provider_name || 'Not Found'} (Nursing Home) - First Bite`
  )

  const [showDoorsModal, setShowDoorsModal] = React.useState(false)

  const entityBreadcrumbs = useMemo(
    () =>
      data?.nursinghome?.provider_name
        ? [{ title: 'Company', label: data.nursinghome.provider_name }]
        : [],
    [data?.nursinghome?.provider_name]
  )

  if (isLoading) {
    return <LoadingScreen />
  }

  // Build operational stats for the summary tab
  const operationalStats: Array<{ label: string; value: React.ReactNode }> = [
    {
      label: 'CCN Number',
      value: data?.nursinghome?.ccn || 'N/A',
    },
    {
      label: 'Certified Beds',
      value:
        data?.nursinghome?.number_of_certified_beds != null
          ? formatInteger(data.nursinghome.number_of_certified_beds)
          : 'N/A',
    },
    {
      label: 'Avg Residents/Day',
      value:
        data?.nursinghome?.average_number_of_residents_per_day != null
          ? formatInteger(data.nursinghome.average_number_of_residents_per_day)
          : 'N/A',
    },
    {
      label: 'Projected Meals/Day',
      value:
        data?.nursinghome?.projected_meals_per_day != null
          ? formatInteger(data.nursinghome.projected_meals_per_day)
          : 'N/A',
    },
  ]

  // Rating and quality stats
  const qualityStats: Array<{ label: string; value: React.ReactNode }> = [
    {
      label: 'Overall Rating',
      value:
        data?.nursinghome?.overall_rating != null ? (
          <Rating value={data.nursinghome.overall_rating} />
        ) : (
          'N/A'
        ),
    },
    {
      label: 'QM Rating',
      value:
        data?.nursinghome?.qm_rating != null ? (
          <Rating value={data.nursinghome.qm_rating} />
        ) : (
          'N/A'
        ),
    },
    {
      label: 'Health Inspection Rating',
      value:
        data?.nursinghome?.health_inspection_rating != null ? (
          <Rating value={data.nursinghome.health_inspection_rating} />
        ) : (
          'N/A'
        ),
    },
    {
      label: 'Staffing Rating',
      value:
        data?.nursinghome?.staffing_rating != null ? (
          <Rating value={data.nursinghome.staffing_rating} />
        ) : (
          'N/A'
        ),
    },
    {
      label: 'Long Stay QM Rating',
      value:
        data?.nursinghome?.long_stay_qm_rating != null ? (
          <Rating value={data.nursinghome.long_stay_qm_rating} />
        ) : (
          'N/A'
        ),
    },
  ]

  // Organization info stats
  const organizationStats: Array<{ label: string; value: React.ReactNode }> = [
    {
      label: 'Ownership Type',
      value: data?.nursinghome?.ownership_type || 'N/A',
    },
    {
      label: 'Provider Type',
      value: data?.nursinghome?.provider_type || 'N/A',
    },
    {
      label: 'Legal Business Name',
      value: data?.nursinghome?.legal_business_name || 'N/A',
    },
    {
      label: 'Affiliated Entity Name',
      value: data?.nursinghome?.affiliated_entity_name || 'N/A',
    },
    {
      label: 'Continuing Care Retirement Community',
      value: data?.nursinghome?.continuing_care_retirement_community || 'N/A',
    },
  ]

  // Build summary tab content
  const summaryTabContent: React.ReactNode[] = []

  // Description and Contact Info
  summaryTabContent.push(
    <DescriptionAndContactInfo
      key="description-contact"
      phone={data?.nursinghome?.telephone_number ?? undefined}
      googlePlaceUrl={data?.nursinghome?.google_place_url || undefined}
      address={
        formatAddress({
          street: data?.nursinghome?.address,
          city: data?.nursinghome?.city,
          state: data?.nursinghome?.state,
          zip: data?.nursinghome?.zip_code,
        }) ?? undefined
      }
    />
  )

  if (data?.doors_count && data?.doors_count > 0) {
    summaryTabContent.push(
      <MetricCard
        key="doors"
        value={data?.doors_count}
        label="Door Count"
        actionText="View Doors"
        onActionClick={() => setShowDoorsModal(true)}
      />
    )
  }

  // Operational Stats Section
  summaryTabContent.push(
    <StatsCard key="operational-stats" stats={operationalStats} />
  )
  summaryTabContent.push(<Divider key="divider-1" />)

  // Quality Stats Section
  summaryTabContent.push(<StatsCard key="quality-stats" stats={qualityStats} />)
  summaryTabContent.push(<Divider key="divider-2" />)

  // Organization Stats Section
  summaryTabContent.push(
    <StatsCard key="organization-stats" stats={organizationStats} />
  )

  // Build location details tab content
  const locationDetailsTabContent: React.ReactNode[] = []

  // Nursing Home Location - similar to other pages
  const nursingHomeLocation = data?.nursinghome?.address
    ? `${data.nursinghome.city}, ${data.nursinghome.state} - USA`
    : 'N/A'

  locationDetailsTabContent.push(
    <MetricCard
      key="nursinghome-location"
      value={nursingHomeLocation}
      label="Nursing Home Location"
      variant="full-width"
    />
  )

  // Location Details Stats
  const locationInfoStats: Array<{ label: string; value: React.ReactNode }> = [
    {
      label: 'Full Address',
      value: data?.nursinghome?.address || 'N/A',
    },
    {
      label: 'ZIP Code',
      value: data?.nursinghome?.zip_code || 'N/A',
    },
    {
      label: 'State',
      value: data?.nursinghome?.state || 'N/A',
    },
    {
      label: 'City',
      value: data?.nursinghome?.city || 'N/A',
    },
    {
      label: 'Website',
      value: data?.nursinghome?.website_url || 'N/A',
    },
    {
      label: 'Date First Approved',
      value: data?.nursinghome?.date_first_approved
        ? new Date(data.nursinghome.date_first_approved).toLocaleDateString()
        : 'N/A',
    },
  ]

  locationDetailsTabContent.push(
    <StatsCard key="location-info" stats={locationInfoStats} />
  )

  if (data?.nursinghome?.address_info) {
    locationDetailsTabContent.push(
      <Divider key="divider-3" />,
      <DemographicsSection
        key="zipcode-demographics"
        addressInfo={data?.nursinghome?.address_info}
      />
    )
  }

  return (
    <>
      <CompanyPageLayout
        chainProxyId={data!.id!}
        breadcrumbRoot="Healthcare - Nursing Home"
        breadcrumbs={[
          {
            title: 'Nursing Home',
            breadcrumb: data!.nursinghome!.provider_name!,
            url: undefined,
          },
        ]}
        entityBreadcrumbs={entityBreadcrumbs}
        categoryInfo={{
          categorySlug: 'non-commercial',
          subCategorySlug: 'healthcare-nursinghomes',
        }}
        sidebar={{
          header: {
            name: data!.nursinghome!.provider_name!,
            domain: data!.nursinghome!.domain || '',
            logoUrl: undefined, // Nursing homes don't have logos in the data
          },
          tabs: [
            {
              key: 'summary',
              title: 'Summary',
              content: summaryTabContent,
            },
            {
              key: 'location-details',
              title: 'Location Details',
              content: locationDetailsTabContent,
            },
          ],
        }}
      >
        <CompanyContentSections
          chainProxyId={data!.id!}
          entityID={nursingHomeID}
          companyName={data!.nursinghome!.provider_name!}
          websiteAddress={data!.nursinghome!.domain || ''}
          companyTypeSlug="healthcare-nursinghomes"
          tableTypeSlug="healthcare-nursinghomes"
        />
      </CompanyPageLayout>

      {/* Unmatched Doors Modal */}
      {data && (
        <UnmatchedDoorsModal
          show={showDoorsModal}
          onHide={() => setShowDoorsModal(false)}
          forChainProxyId={data.id}
          chainData={{
            id: data.id,
            name: data.nursinghome!.provider_name!,
            domain: data.nursinghome?.domain || undefined,
          }}
          subCategory={{
            name: 'Healthcare - Nursing Homes',
            slug: 'healthcare-nursinghomes',
            description: 'Healthcare - Nursing Homes',
          }}
          companyName={data.nursinghome?.provider_name}
        />
      )}
    </>
  )
}
